import React from 'react';
import { ChevronDown, ChevronRight, Trash2, Target, CheckCircle, Loader2, Play, Clock, GraduationCap } from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { UnitDto, LessonDetailDto } from './types';
import { AssessmentCard } from './AssessmentCard';
import { LessonCard } from './LessonCard';
import { useEvent, useLazyCommand } from '@soap/modules';
import { useSelectedStudent } from '../../hooks/useSelectedStudent';
import { ShareByEmailDialog } from '../ShareByEmailDialog';
import { DeleteUnitDialog } from '../dialogs/DeleteUnitDialog';
import { UnitCompletionDialog } from '../dialogs/UnitCompletionDialog';
import { LessonSessionDialog } from '../dialogs/LessonSessionDialog';
import { LessonCompletionDialog } from '../dialogs/LessonCompletionDialog';
import {
    lessonsUnitElementId,
    scheduleScrollLessonCardIntoView,
    scheduleScrollUnitCardIntoView
} from '../LessonsPage/utils/lessonUnitNavigation';
import { UnitSectionHeading } from './UnitSectionHeading';
import {
    hasOpenSession,
    isLessonCompletedDto,
    isLessonCompletedKey,
    isLessonContentComplete,
    isLessonInProgressKey,
    isLessonReadyKey,
    isLessonStartedNotCompletedDto,
    LessonStateKey,
    UnitStateKey
} from './lessonStepState';
import { UnitHeaderSubjectAndTopics, UnitHeaderSubjectLine } from '../UnitHeaderTopicLabels';
import type { UnitHeaderTone } from '../UnitHeaderTopicLabels';
import { UnitHeaderTopicsWithObjectives } from '../UnitHeaderTopicsWithObjectives';
import { UnitCardCreatedByMeta } from '../UnitCardCreatedByMeta';
import { UnitCardHeaderNewBadge } from '../UnitCardHeaderNewBadge';
import { deriveUnitHeaderFields } from '../unitHeaderDisplay';
import { formatTimeDisplay } from '../../utils/timeUtils';
import {
    unitCardBodyClass,
    unitCardBodyMetaClass,
    unitCardHeaderClass,
    unitCardShellClass,
} from '../unitCardTheme';
import { UnitCardHeaderProgressBars } from './UnitCardHeaderProgressBars';
import { lessonLoadingStateKey, normalizeIdPart } from './loadingStateKeys';
import { cn } from '../../lib/utils';

function mergeLessonWithUnitHeaderPatch(
    lesson: LessonDetailDto,
    optimistic: { lessonId: string; patch: Partial<LessonDetailDto> } | null
): LessonDetailDto {
    if (!optimistic || normalizeIdPart(lesson.e251_LessonId) !== normalizeIdPart(optimistic.lessonId)) {
        return lesson;
    }
    return { ...lesson, ...optimistic.patch };
}

interface UnitCardProps {
    unit: UnitDto;
    expandedUnits: Set<string>;
    loadingStates: Set<string>;
    onToggleUnit: (unitId: string) => void;
    onCreateAssessment?: (unitId: string, topicIds: string[], unitTitle?: string) => void;
    setLoadingStates: React.Dispatch<React.SetStateAction<Set<string>>>;
    onRefresh?: () => void;
    passFailThreshold?: number | null;
    /** When set, only this lesson stays visible while session view is active. */
    sessionViewLessonId?: string | null;
    /** Session view visibility changed for a lesson on this unit. */
    onSessionViewChange?: (lessonId: string, focused: boolean) => void;
}

export const UnitCard = React.memo(({ 
    unit,
    expandedUnits,
    loadingStates,
    onToggleUnit,
    onCreateAssessment,
    setLoadingStates,
    onRefresh,
    passFailThreshold = null,
    sessionViewLessonId = null,
    onSessionViewChange
}: UnitCardProps) => {
    const viewStates = {
        normal: 0,
        deletingUnit: 1,
        completingUnit: 2
    };
    
    const [viewState, setViewState] = React.useState(viewStates.normal);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [headerLessonDialog, setHeaderLessonDialog] = React.useState<
        null | { kind: 'start' | 'continue' | 'finish'; lesson: LessonDetailDto }
    >(null);
    const [unitHeaderLessonOptimistic, setUnitHeaderLessonOptimistic] = React.useState<{
        lessonId: string;
        patch: Partial<LessonDetailDto>;
    } | null>(null);
    const [shareByEmailDialogOpen, setShareByEmailDialogOpen] = React.useState(false);
    const [isHeaderShareWorking, setIsHeaderShareWorking] = React.useState(false);

    const { selectedStudentId, students } = useSelectedStudent();
    const [shareByStudent] = useLazyCommand('C203v1_ShareByStudent');
    const [shareByLink] = useLazyCommand('C201v1_ShareByLink');

    const unitId = unit.e251_UnitId || '';

    const isUnitExpanded = expandedUnits.has(unitId);
    const isUnitExpandedRef = React.useRef(isUnitExpanded);
    React.useEffect(() => {
        isUnitExpandedRef.current = isUnitExpanded;
    }, [isUnitExpanded]);

    const [expandedLessons, setExpandedLessons] = React.useState<Set<string>>(new Set());
    const [expandedAssessments, setExpandedAssessments] = React.useState<Set<string>>(new Set());
    /** When Continue/Get Started schedules a lesson scroll, skip the generic unit-expand scroll. */
    const skipNextUnitExpandScrollRef = React.useRef(false);

    const expandUnitAndAssessmentSection = React.useCallback(() => {
        const assessmentId = unit.e251_AssessmentId;
        if (!assessmentId) return;
        if (!isUnitExpandedRef.current) {
            onToggleUnit(unitId);
        }
        setExpandedAssessments(prev => new Set(prev).add(assessmentId));
    }, [unitId, unit.e251_AssessmentId, onToggleUnit]);
    
    // Ref for scrolling to unit card
    const unitCardRef = React.useRef<HTMLDivElement>(null);
    
    const toggleLesson = (lessonId: string) => {
        setExpandedLessons(prev => {
            if (prev.has(lessonId)) {
                return new Set();
            }
            return new Set([lessonId]);
        });
    };
    
    const collapseAllLessonsExcept = (lessonIdToKeep: string) => {
        // Collapse all lessons except the one being finished
        setExpandedLessons(new Set([lessonIdToKeep]));
    };

    const collapseAllLessons = React.useCallback(() => {
        setExpandedLessons(new Set());
    }, []);

    const lessons = React.useMemo(
        () =>
            (unit.e251_Lessons || []).map(l =>
                mergeLessonWithUnitHeaderPatch(l, unitHeaderLessonOptimistic)
            ),
        [unit.e251_Lessons, unitHeaderLessonOptimistic]
    );

    const lessonsRef = React.useRef(lessons);
    lessonsRef.current = lessons;

    const handleLessonFinishedEffects = React.useCallback(
        (finishedLessonId: string) => {
            collapseAllLessons();

            const unitLessons = lessonsRef.current;
            const finishingLastLessonInUnit =
                unitLessons.length > 0 &&
                unitLessons.every(l => {
                    const id = normalizeIdPart(l.e251_LessonId);
                    if (id === normalizeIdPart(finishedLessonId)) return true;
                    return isLessonCompletedDto(l);
                });

            if (finishingLastLessonInUnit) {
                scheduleScrollUnitCardIntoView(unitId, {
                    delayMs: 280,
                    block: 'start',
                    force: true
                });
            } else {
                scheduleScrollLessonCardIntoView(unitId, finishedLessonId, {
                    delayMs: 280,
                    block: 'center',
                    force: true
                });
            }
        },
        [collapseAllLessons, unitId]
    );

    const [pendingHeaderLessonFollowUp, setPendingHeaderLessonFollowUp] = React.useState<
        null | { kind: 'expand-lesson'; lessonId: string }
    >(null);

    React.useEffect(() => {
        if (!pendingHeaderLessonFollowUp) return;
        const lid = pendingHeaderLessonFollowUp.lessonId;
        const k = lessonLoadingStateKey(unitId, lid);
        if (loadingStates.has(k)) return;

        setExpandedLessons(new Set([lid]));
        setPendingHeaderLessonFollowUp(null);
    }, [loadingStates, pendingHeaderLessonFollowUp, unitId]);

    const headerLessonMergedForDialog = React.useMemo(() => {
        if (!headerLessonDialog) return null;
        const id = headerLessonDialog.lesson.e251_LessonId;
        const live = (unit.e251_Lessons || []).find(
            l => normalizeIdPart(l.e251_LessonId) === normalizeIdPart(id)
        );
        const base = live ?? headerLessonDialog.lesson;
        return mergeLessonWithUnitHeaderPatch(base, unitHeaderLessonOptimistic);
    }, [headerLessonDialog, unit.e251_Lessons, unitHeaderLessonOptimistic]);

    React.useEffect(() => {
        if (!unitHeaderLessonOptimistic) return;
        const raw = unit.e251_Lessons || [];
        const row = raw.find(
            l =>
                normalizeIdPart(l.e251_LessonId) === normalizeIdPart(unitHeaderLessonOptimistic.lessonId)
        );
        if (!row) return;

        const p = unitHeaderLessonOptimistic.patch;

        if (p.e251_LessonState === LessonStateKey.Completed) {
            if (isLessonCompletedKey(row.e251_LessonState) && row.e251_LessonCompletionDate != null) {
                setUnitHeaderLessonOptimistic(null);
            }
            return;
        }

        if (p.e251_LessonState === LessonStateKey.InProgress && isLessonInProgressKey(row.e251_LessonState)) {
            setUnitHeaderLessonOptimistic(null);
        }
    }, [unit.e251_Lessons, unitHeaderLessonOptimistic]);

    const handleHeaderRevertOptimistic = React.useCallback(() => {
        setUnitHeaderLessonOptimistic(null);
    }, []);

    const handleHeaderSessionApplyOptimistic = React.useCallback((snapshot: LessonDetailDto) => {
        const id = snapshot.e251_LessonId;
        if (!id) return;
        setUnitHeaderLessonOptimistic({
            lessonId: id,
            patch: { e251_LessonState: LessonStateKey.InProgress }
        });
    }, []);
    
    // Listen for unit deleted event to clear isDeleting state
    useEvent({
        eventName: 'E255v1_UnitDeleted',
        onEventReceived: (event: any) => {
            if (event.e255_UnitId === unitId) {
                setIsDeleting(false);
            }
        }
    });
    
    // Listen for lesson finished event - collapse all lessons and scroll into view
    useEvent({
        eventName: 'E261v1_LessonFinished',
        onEventReceived: (event: any) => {
            const eventUnitId = event.e261_UnitId ?? event.E261_UnitId;
            if (normalizeIdPart(String(eventUnitId ?? '')) !== normalizeIdPart(unitId)) {
                return;
            }
            const lessonId = String(event.e261_LessonId ?? event.E261_LessonId ?? '');
            if (!lessonId) return;
            handleLessonFinishedEffects(lessonId);
        }
    });
    
    const toggleAssessment = (assessmentId: string) => {
        setExpandedAssessments(prev => {
            const newSet = new Set(prev);
            if (newSet.has(assessmentId)) {
                newSet.delete(assessmentId);
            } else {
                newSet.add(assessmentId);
            }
            return newSet;
        });
    };
    const { topics, subjects, learningStyles, totalDuration, createdAt, createdByName } =
        deriveUnitHeaderFields(unit);
    /** Unit-level work only (e.g. abandon). Lesson/activity keys use `${unitId}-…` and are handled on those cards. */
    const isUnitLoading = React.useMemo(() => loadingStates.has(unitId), [loadingStates, unitId]);
    
    // Check if this is an assessment-only unit (no lessons)
    // This includes units that were assessment-only but may have had their assessment deleted
    const isAssessmentOnlyUnit = lessons.length === 0;
    const assessmentOnlyHeaderTrailing = isAssessmentOnlyUnit ? (
        <span className="text-base font-semibold font-sans text-confirm-action-dark">(Assessment Only)</span>
    ) : undefined;

    // Determine if unit is finished
    const isUnitFinished = unit.e251_State === UnitStateKey.Complete;
    
    // Determine if assessment is teacher-approved (not just started)
    const isAssessmentTeacherApproved = unit.e251_AssessmentStatus?.e251_Result?.e251_ResultState?.key === 'teacher-approved';
    
    // Show Abandon button until unit is finished
    // For assessment-only units: show even when teacher-approved (until unit is finished)
    // For regular units: hide when teacher-approved
    const shouldShowAbandon = !isUnitFinished && (!isAssessmentTeacherApproved || isAssessmentOnlyUnit);
    
    const isLessonCompleted = isLessonCompletedDto;

    const isLessonStartedNotCompleted = isLessonStartedNotCompletedDto;

    // Determine if any lesson has been started (in progress)
    const hasLessonStarted = lessons.some(lesson => isLessonInProgressKey(lesson.e251_LessonState));

    const hasAnyLessonCompleted = lessons.some(isLessonCompleted);
    
    // Check if all lessons are completed
    const allLessonsCompleted = lessons.length > 0 && lessons.every(isLessonCompleted);
    
    // Check if assessment-only unit has an assessment (either currently assigned or in the past)
    const hasAssessment = unit.e251_AssessmentId != null;
    
    // Check if assessment has been assigned (either SharedAssessment or Result exists)
    const isAssessmentAssigned = unit.e251_AssessmentStatus?.e251_SharedAssessment != null || 
                                  unit.e251_AssessmentStatus?.e251_Result != null;
    
    // Assessment-only units without an assessment should not show Get Started button
    const isAssessmentOnlyUnitWithoutAssessment = isAssessmentOnlyUnit && !hasAssessment;
    
    // Get Started/Continue button text
    // For assessment-only units: show "Get Started" until assessment is assigned, then "Continue"
    // For regular units: Show "Continue" if lessons have been started OR if unit is in assess stage
    const isAssessing = unit.e251_State === UnitStateKey.Assessing;
    // Collapsed header Get Started/Continue: show while work remains; hide once all lessons are done
    // and the assessment is marked (Finish Unit takes over).
    const showCollapsedHeaderContinue =
        !isUnitExpanded &&
        !isAssessmentOnlyUnitWithoutAssessment &&
        (!allLessonsCompleted || (isAssessing && !isAssessmentTeacherApproved));
    let getStartedButtonText = 'Get Started';
    if (isAssessmentOnlyUnit && hasAssessment) {
        getStartedButtonText = isAssessmentAssigned ? 'Continue' : 'Get Started';
    } else {
        getStartedButtonText =
            hasLessonStarted || isAssessing || hasAnyLessonCompleted ? 'Continue' : 'Get Started';
    }

    const assessButtonVisible =
        !unit.e251_AssessmentId &&
        !!onCreateAssessment &&
        ((!isAssessmentOnlyUnit &&
            unit.e251_State === UnitStateKey.LessonsCompleted) ||
            (isAssessmentOnlyUnitWithoutAssessment && !isUnitFinished));

    const finishUnitButtonText = assessButtonVisible
        ? 'Finish Unit without assessing'
        : 'Finish Unit';
    
    // Handle unit toggle - collapse lessons when collapsing unit
    const handleUnitToggle = () => {
        // If unit is currently expanded and we're collapsing it, also collapse all lessons
        if (isUnitExpanded) {
            setExpandedLessons(new Set());
            setExpandedAssessments(new Set());
        }
        onToggleUnit(unitId);
    };

    const runAssignAssessmentFromHeader = React.useCallback(async (): Promise<'assigned' | 'email-pending' | 'noop'> => {
        if (!unit.e251_AssessmentId || !selectedStudentId) return 'noop';
        const selectedStudent = students.find(s => s.id === selectedStudentId);
        if (selectedStudent?.hasOwnAccount) {
            await shareByStudent({
                C203_AssessmentId: unit.e251_AssessmentId,
                C203_UnitId: unit.e251_UnitId,
                C203_StudentId: selectedStudentId,
                C203_Expiry: null
            });
            return 'assigned';
        }
        if (selectedStudent?.email) {
            setShareByEmailDialogOpen(true);
            return 'email-pending';
        }
        await shareByLink({
            C201_AssessmentId: unit.e251_AssessmentId,
            C201_UnitId: unit.e251_UnitId,
            C201_StudentId: selectedStudentId,
            C201_Expiry: null
        });
        return 'assigned';
    }, [unit.e251_AssessmentId, unit.e251_UnitId, selectedStudentId, students, shareByStudent, shareByLink]);

    const scheduleScrollToLesson = React.useCallback(
        (lessonIdRaw: string) => {
            const lessonIdPart = (lessonIdRaw || '').trim();
            if (!lessonIdPart) return;
            scheduleScrollLessonCardIntoView(unitId, lessonIdPart, {
                delayMs: 280,
                block: 'center',
                force: true
            });
        },
        [unitId]
    );

    const expandUnitForContinueScroll = React.useCallback(() => {
        if (isUnitExpanded) return;
        // Prefer centering the lesson/activity over the generic unit-expand scroll.
        skipNextUnitExpandScrollRef.current = true;
        onToggleUnit(unitId);
    }, [isUnitExpanded, onToggleUnit, unitId]);

    const handleGetStartedOrContinueClick = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setHeaderLessonDialog(null);

        if (isAssessmentOnlyUnit && hasAssessment && unit.e251_AssessmentId) {
            const shared = !!unit.e251_AssessmentStatus?.e251_SharedAssessment;
            const started = !!unit.e251_AssessmentStatus?.e251_Result;
            if (!shared && !started) {
                setIsHeaderShareWorking(true);
                try {
                    const outcome = await runAssignAssessmentFromHeader();
                    if (outcome === 'assigned') {
                        expandUnitAndAssessmentSection();
                    }
                } finally {
                    setIsHeaderShareWorking(false);
                }
                return;
            }
            if (!isUnitExpanded) onToggleUnit(unitId);
            setExpandedAssessments(prev => new Set(prev).add(unit.e251_AssessmentId!));
            return;
        }

        if (isAssessing && unit.e251_AssessmentId) {
            if (!isUnitExpanded) onToggleUnit(unitId);
            setExpandedAssessments(prev => new Set(prev).add(unit.e251_AssessmentId!));
            return;
        }

        const headerContinueCandidates = lessons.filter(isLessonStartedNotCompleted);
        const explicitInProgressLessons = headerContinueCandidates.filter(l =>
            isLessonInProgressKey(l.e251_LessonState)
        );
        // Prefer lessons explicitly In Progress so completed lessons with stale activity data never win the header dialog.
        const startedIncompleteLessons =
            explicitInProgressLessons.length > 0 ? explicitInProgressLessons : headerContinueCandidates;
        const incompleteLessons = lessons.filter(l => !isLessonCompleted(l));
        const firstIncompleteReady = incompleteLessons.find(l => isLessonReadyKey(l.e251_LessonState));

        if (startedIncompleteLessons.length > 0) {
            expandUnitForContinueScroll();

            // Multiple in-progress: open the unit and scroll to the first candidate (do not expand lesson/activity rows).
            if (startedIncompleteLessons.length > 1) {
                const pick = startedIncompleteLessons[0];
                const pickId = pick.e251_LessonId || '';
                if (!pickId) return;
                scheduleScrollToLesson(pickId);
                return;
            }

            const L = startedIncompleteLessons[0];
            const lessonId = L.e251_LessonId || '';

            if (hasOpenSession(L)) {
                scheduleScrollToLesson(lessonId);
            } else if (isLessonContentComplete(L)) {
                setHeaderLessonDialog({ kind: 'finish', lesson: L });
                scheduleScrollToLesson(lessonId);
            } else {
                setHeaderLessonDialog({ kind: 'continue', lesson: L });
                scheduleScrollToLesson(lessonId);
            }
            return;
        }

        if (firstIncompleteReady) {
            expandUnitForContinueScroll();
            setHeaderLessonDialog({ kind: 'start', lesson: firstIncompleteReady });
            return;
        }

        const firstIncomplete = incompleteLessons[0];
        if (firstIncomplete) {
            const lessonId = firstIncomplete.e251_LessonId || '';
            expandUnitForContinueScroll();
            scheduleScrollToLesson(lessonId);
            return;
        }

        handleUnitToggle();
    };

    // Show Starburst based on Touched property (not state)
    const showStarburst = !unit.e251_Touched;
    const unitHeaderTone: UnitHeaderTone = 'default';
    const isSessionViewActive = sessionViewLessonId != null;

    React.useEffect(() => {
        if (!isSessionViewActive) return;
        if (!isUnitExpanded) {
            onToggleUnit(unitId);
        }
    }, [isSessionViewActive, isUnitExpanded, onToggleUnit, unitId]);

    const wasUnitExpandedRef = React.useRef(isUnitExpanded);
    React.useEffect(() => {
        const justExpanded = isUnitExpanded && !wasUnitExpandedRef.current;
        wasUnitExpandedRef.current = isUnitExpanded;
        if (!justExpanded || !unitId || isSessionViewActive) return;
        if (skipNextUnitExpandScrollRef.current) {
            skipNextUnitExpandScrollRef.current = false;
            return;
        }

        const handle = scheduleScrollUnitCardIntoView(unitId);
        return () => window.clearTimeout(handle);
    }, [isUnitExpanded, unitId, isSessionViewActive]);

    return (
        <Card
            ref={unitCardRef}
            key={unitId}
            id={unitId ? lessonsUnitElementId(unitId) : undefined}
            className={cn(
                unitCardShellClass,
                'relative',
                isSessionViewActive && 'border-0 bg-transparent shadow-none ring-0'
            )}
        >
            <div className={isUnitLoading ? 'opacity-50 pointer-events-none' : ''}>
            {/* Keep header mounted (hidden) so the lessons body / LessonCard are not remounted on session view. */}
            <div hidden={isSessionViewActive}>
            {/* New Unit Badge */}
            <UnitCardHeaderNewBadge visible={showStarburst} />
            {/* Unit Header */}
            <div
                onClick={handleUnitToggle}
                className={unitCardHeaderClass}
            >
                    {/* Mobile: Buttons on top */}
                    <div className="md:hidden mb-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-col gap-2">
                        {/* Show Get Started/Continue when collapsed and work remains (not when assessment is marked) */}
                        {showCollapsedHeaderContinue && (
                            <Button
                                variant="default"
                                size="sm"
                                onClick={(e) => { void handleGetStartedOrContinueClick(e); }}
                                disabled={isHeaderShareWorking}
                                className="bg-action-blue hover:bg-action-blue-dark text-white"
                            >
                                {isHeaderShareWorking ? (
                                    <Loader2 size={16} className="mr-2 animate-spin" aria-hidden />
                                ) : (
                                    <Play size={16} className="mr-2" aria-hidden />
                                )}
                                {getStartedButtonText}
                            </Button>
                        )}
                        
                        {/* Show Assess button when:
                            - Regular units: state is LessonsCompleted AND no assessment
                            - Assessment-only units without assessment: any state (as long as not completed) */}
                        {assessButtonVisible && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    if (onCreateAssessment) {
                                        const topicIds = (unit.e251_TopicIds || []).filter(id => id) as string[];
                                        // Use topics list as title to match the card header display
                                        const unitTitle = topics.join(', ') || unit.e251_Title || 'Untitled Unit';
                                        onCreateAssessment(unitId, topicIds, unitTitle);
                                    }
                                }}
                                disabled={isHeaderShareWorking}
                                className="text-action-blue border-action-blue/30 hover:bg-action-blue/10"
                            >
                                <Target size={16} className="mr-2" />
                                Assess
                            </Button>
                        )}
                        
                        {/* Show Finish Unit button when:
                            - All lessons completed AND no assessment, OR
                            - All lessons completed AND assessment is teacher-approved, OR
                            - Unit is in Assessing state AND assessment is teacher-approved
                            Hide for assessment-only units without an assessment */}
                        {!isAssessmentOnlyUnitWithoutAssessment && 
                         (((unit.e251_State === UnitStateKey.LessonsCompleted) && 
                            (!unit.e251_AssessmentId || isAssessmentTeacherApproved)) ||
                         ((unit.e251_State === UnitStateKey.Assessing) && 
                            isAssessmentTeacherApproved && 
                            ((isAssessmentOnlyUnit && hasAssessment) || allLessonsCompleted))) && (
                            <Button
                                variant="default"
                                size="sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('Finish Unit button clicked, setting viewState to completingUnit');
                                    setViewState(viewStates.completingUnit);
                                }}
                                disabled={loadingStates.has(`finish-${unitId}`) || isHeaderShareWorking}
                                className="bg-action-blue hover:bg-action-blue-dark text-white"
                            >
                                <CheckCircle size={16} className="mr-2" />
                                {finishUnitButtonText}
                            </Button>
                        )}
                        
                        {/* Show Abandon button until unit is finished OR assessment is completed */}
                        {shouldShowAbandon && viewState !== viewStates.deletingUnit && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setViewState(viewStates.deletingUnit);
                                    setIsDeleting(true);
                                }}
                                disabled={loadingStates.has(unitId) || isHeaderShareWorking}
                                className="text-destructive-action border-destructive-action/30 hover:bg-destructive-action/10"
                            >
                                <Trash2 size={16} className="mr-2" />
                                Abandon Unit
                            </Button>
                        )}
                        
                        {/* Show deleting state */}
                        {viewState === viewStates.deletingUnit && (
                            <Button
                                variant="outline"
                                size="sm"
                                disabled
                                className="text-destructive-action border-destructive-action/30 hover:bg-destructive-action/10 opacity-50"
                            >
                                <Loader2 size={16} className="mr-2 animate-spin" />
                                Abandoning Unit...
                            </Button>
                        )}
                    </div>
                </div>

                {/* Title and details: topic pills flow full-width under header buttons on md+ */}
                <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between gap-4">
                        <div className="flex min-w-0 flex-1 items-center">
                            {isUnitExpanded ? (
                                <ChevronDown size={20} className="mr-3 shrink-0 self-center text-gray-600" />
                            ) : (
                                <ChevronRight size={20} className="mr-3 shrink-0 self-center text-gray-600" />
                            )}
                            <div className="flex min-w-0 flex-1 flex-col gap-3">
                                <h3 className="m-0 text-lg font-normal text-gray-900 md:hidden">
                                    <UnitHeaderSubjectAndTopics
                                        subjects={subjects}
                                        topics={topics}
                                        tone={unitHeaderTone}
                                        trailing={assessmentOnlyHeaderTrailing}
                                        showTopicsLabel={false}
                                        learningObjectives={unit.e251_LearningObjectives}
                                        showObjectiveStatus={isUnitFinished}
                                    />
                                </h3>
                                <h3 className="m-0 hidden text-lg font-normal text-gray-900 md:block">
                                    <UnitHeaderSubjectLine
                                        subjects={subjects}
                                        tone={unitHeaderTone}
                                        trailing={assessmentOnlyHeaderTrailing}
                                    />
                                </h3>
                                <div className="flex flex-col gap-3 md:hidden">
                                    <UnitCardHeaderProgressBars
                                        lessons={lessons}
                                        assessmentId={unit.e251_AssessmentId}
                                        assessmentStatus={unit.e251_AssessmentStatus}
                                        tone={unitHeaderTone}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Tablet/Desktop: Buttons on right (stacked vertically on tablet, horizontal on desktop) */}
                        <div
                            className="hidden shrink-0 self-center md:flex md:flex-col lg:flex-row md:items-stretch lg:items-center gap-2 ml-4"
                            onClick={(e) => e.stopPropagation()}
                        >
                        {/* Show Get Started/Continue when collapsed and work remains (not when assessment is marked) */}
                        {showCollapsedHeaderContinue && (
                            <Button
                                variant="default"
                                size="sm"
                                onClick={(e) => { void handleGetStartedOrContinueClick(e); }}
                                disabled={isHeaderShareWorking}
                                className="bg-action-blue hover:bg-action-blue-dark text-white"
                            >
                                {isHeaderShareWorking ? (
                                    <Loader2 size={16} className="mr-2 animate-spin" aria-hidden />
                                ) : (
                                    <Play size={16} className="mr-2" aria-hidden />
                                )}
                                {getStartedButtonText}
                            </Button>
                        )}
                        
                        {/* Show Assess button when:
                            - Regular units: state is LessonsCompleted AND no assessment
                            - Assessment-only units without assessment: any state (as long as not completed) */}
                        {assessButtonVisible && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    if (onCreateAssessment) {
                                        const topicIds = (unit.e251_TopicIds || []).filter(id => id) as string[];
                                        // Use topics list as title to match the card header display
                                        const unitTitle = topics.join(', ') || unit.e251_Title || 'Untitled Unit';
                                        onCreateAssessment(unitId, topicIds, unitTitle);
                                    }
                                }}
                                disabled={isHeaderShareWorking}
                                className="text-action-blue border-action-blue/30 hover:bg-action-blue/10"
                            >
                                <Target size={16} className="mr-2" />
                                Assess
                            </Button>
                        )}
                        
                        {/* Show Finish Unit button when:
                            - All lessons completed AND no assessment, OR
                            - All lessons completed AND assessment is teacher-approved, OR
                            - Unit is in Assessing state AND assessment is teacher-approved
                            Hide for assessment-only units without an assessment */}
                        {!isAssessmentOnlyUnitWithoutAssessment && 
                         (((unit.e251_State === UnitStateKey.LessonsCompleted) && 
                            (!unit.e251_AssessmentId || isAssessmentTeacherApproved)) ||
                         ((unit.e251_State === UnitStateKey.Assessing) && 
                            isAssessmentTeacherApproved && 
                            ((isAssessmentOnlyUnit && hasAssessment) || allLessonsCompleted))) && (
                            <Button
                                variant="default"
                                size="sm"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    console.log('Finish Unit button clicked, setting viewState to completingUnit');
                                    setViewState(viewStates.completingUnit);
                                }}
                                disabled={loadingStates.has(`finish-${unitId}`) || isHeaderShareWorking}
                                className="bg-action-blue hover:bg-action-blue-dark text-white"
                            >
                                <CheckCircle size={16} className="mr-2" />
                                {finishUnitButtonText}
                            </Button>
                        )}
                        
                        {/* Show Abandon button until unit is finished OR assessment is completed */}
                        {shouldShowAbandon && viewState !== viewStates.deletingUnit && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    setViewState(viewStates.deletingUnit);
                                    setIsDeleting(true);
                                }}
                                disabled={loadingStates.has(unitId) || isHeaderShareWorking}
                                className="text-destructive-action border-destructive-action/30 hover:bg-destructive-action/10"
                            >
                                <Trash2 size={16} className="mr-2" />
                                Abandon Unit
                            </Button>
                        )}
                        
                        {/* Show deleting state */}
                        {viewState === viewStates.deletingUnit && (
                            <Button
                                variant="outline"
                                size="sm"
                                disabled
                                className="text-destructive-action border-destructive-action/30 hover:bg-destructive-action/10 opacity-50"
                            >
                                <Loader2 size={16} className="mr-2 animate-spin" />
                                Abandoning Unit...
                            </Button>
                        )}
                    </div>
                </div>
                    <div className="hidden min-w-0 w-full flex-col gap-3 pl-8 md:flex">
                        <UnitHeaderTopicsWithObjectives
                            topics={topics}
                            learningObjectives={unit.e251_LearningObjectives}
                            showStatus={isUnitFinished}
                            tone={unitHeaderTone}
                        />
                        <UnitCardHeaderProgressBars
                            lessons={lessons}
                            assessmentId={unit.e251_AssessmentId}
                            assessmentStatus={unit.e251_AssessmentStatus}
                            tone={unitHeaderTone}
                        />
                    </div>
                </div>
            </div>
            </div>

            {/* Unit Content */}
            {(isUnitExpanded || isSessionViewActive) && (
                <div className={cn(!isSessionViewActive && unitCardBodyClass)}>
                    <div className={cn(!isSessionViewActive && 'p-6')}>
                        <div
                            hidden={isSessionViewActive}
                            className={
                                learningStyles.length > 0 || totalDuration > 0 || createdAt || createdByName
                                    ? undefined
                                    : 'hidden'
                            }
                        >
                            {(learningStyles.length > 0 || totalDuration > 0 || createdAt || createdByName) && (
                            <div className={unitCardBodyMetaClass}>
                                {totalDuration > 0 && (
                                    <span className="flex items-center">
                                        <Clock size={14} className="mr-1 text-gray-600" />
                                        {formatTimeDisplay(totalDuration)}
                                    </span>
                                )}
                                {learningStyles.length > 0 && (
                                    <span className="flex items-center">
                                        <GraduationCap size={14} className="mr-1 text-gray-600" />
                                        {learningStyles.join(', ')}
                                    </span>
                                )}
                                <UnitCardCreatedByMeta
                                    createdAt={createdAt}
                                    createdByName={createdByName}
                                    tone="default"
                                />
                            </div>
                            )}
                        </div>
                        {/* Assessment Card */}
                        <div hidden={isSessionViewActive || !unit.e251_AssessmentId}>
                            {unit.e251_AssessmentId && (
                            <AssessmentCard
                                unit={unit}
                                status={unit.e251_AssessmentStatus}
                                isExpanded={expandedAssessments.has(unit.e251_AssessmentId || '')}
                                onToggle={toggleAssessment}
                                onRefresh={onRefresh}
                                isDeleting={isDeleting}
                                passFailThreshold={passFailThreshold}
                            />
                            )}
                        </div>


                        {/* Lessons — always visible under a static heading */}
                        <div>
                            <UnitSectionHeading
                                title={`Lessons (${lessons.length})`}
                                hidden={isSessionViewActive}
                            />
                            <div className="space-y-3">
                                {lessons.map((lesson) => {
                                    const lid = lesson.e251_LessonId || '';
                                    const isThisLessonSessionView =
                                        sessionViewLessonId != null &&
                                        normalizeIdPart(lid) === normalizeIdPart(sessionViewLessonId);
                                    return (
                                        <div
                                            key={lesson.e251_LessonId}
                                            hidden={sessionViewLessonId != null && !isThisLessonSessionView}
                                        >
                                        <LessonCard
                                            lesson={lesson}
                                            unitId={unitId}
                                            isExpanded={expandedLessons.has(lid)}
                                            loadingStates={loadingStates}
                                            onToggle={toggleLesson}
                                            setLoadingStates={setLoadingStates}
                                            onCollapseAllExcept={collapseAllLessonsExcept}
                                            onLessonFinished={collapseAllLessons}
                                            onSessionViewChange={focused => {
                                                onSessionViewChange?.(lid, focused);
                                            }}
                                            sessionViewActive={isThisLessonSessionView}
                                        />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            </div>

            {(headerLessonDialog?.kind === 'start' || headerLessonDialog?.kind === 'continue') &&
                headerLessonMergedForDialog && (
                <LessonSessionDialog
                    unitId={unitId}
                    lessonId={headerLessonDialog.lesson.e251_LessonId!}
                    lesson={headerLessonMergedForDialog}
                    mode={headerLessonDialog.kind}
                    setLoadingStates={setLoadingStates}
                    onApplyOptimistic={() =>
                        handleHeaderSessionApplyOptimistic(headerLessonMergedForDialog)
                    }
                    onRevertOptimistic={handleHeaderRevertOptimistic}
                    onClose={() => setHeaderLessonDialog(null)}
                    onSuccess={() => {
                        const lessonId = headerLessonDialog.lesson.e251_LessonId;
                        setHeaderLessonDialog(null);
                        if (!lessonId) return;
                        setPendingHeaderLessonFollowUp({ kind: 'expand-lesson', lessonId });
                    }}
                />
            )}
            {headerLessonDialog?.kind === 'finish' && headerLessonMergedForDialog && (
                <LessonCompletionDialog
                    unitId={unitId}
                    lessonId={headerLessonDialog.lesson.e251_LessonId!}
                    lesson={headerLessonMergedForDialog}
                    setLoadingStates={setLoadingStates}
                    onApplyOptimistic={() => {
                        const id = headerLessonDialog.lesson.e251_LessonId;
                        if (!id) return;
                        setUnitHeaderLessonOptimistic({
                            lessonId: id,
                            patch: {
                                e251_LessonState: LessonStateKey.Completed,
                                e251_LessonCompletionDate: new Date().toISOString()
                            }
                        });
                    }}
                    onRevertOptimistic={handleHeaderRevertOptimistic}
                    onClose={() => setHeaderLessonDialog(null)}
                    onSuccess={() => {
                        const lid = headerLessonDialog.lesson.e251_LessonId;
                        setHeaderLessonDialog(null);
                        if (lid) {
                            collapseAllLessons();
                        }
                    }}
                />
            )}
            
            {shareByEmailDialogOpen && unit.e251_AssessmentId && (
                <ShareByEmailDialog
                    assessmentId={unit.e251_AssessmentId}
                    studentId={selectedStudentId || undefined}
                    unitId={unit.e251_UnitId}
                    open={shareByEmailDialogOpen}
                    onClose={() => setShareByEmailDialogOpen(false)}
                    onSuccess={() => {
                        setShareByEmailDialogOpen(false);
                        expandUnitAndAssessmentSection();
                    }}
                />
            )}
            
            {/* Delete Unit Dialog */}
            {viewState === viewStates.deletingUnit && (
                <DeleteUnitDialog
                    unitId={unitId}
                    unitTitle={topics.join(', ') || unit.e251_Title || 'Untitled Unit'}
                    actionName="Abandon"
                    onConfirm={() => {
                        setLoadingStates(prev => new Set(prev).add(unitId));
                    }}
                    onClose={() => setViewState(viewStates.normal)}
                />
            )}
            
            {/* Unit Completion Dialog */}
            {viewState === viewStates.completingUnit && (
                <UnitCompletionDialog
                    unitId={unitId}
                    unitTitle={topics.join(', ') || unit.e251_Title || 'Untitled Unit'}
                    learningObjectives={unit.e251_LearningObjectives || []}
                    onClose={() => {
                        console.log('UnitCompletionDialog onClose called');
                        setViewState(viewStates.normal);
                    }}
                    onSuccess={() => {
                        console.log('UnitCompletionDialog onSuccess called');
                        setViewState(viewStates.normal);
                        // Expand the unit if it's not already expanded to show learning objective status
                        if (!isUnitExpanded) {
                            onToggleUnit(unitId);
                        }
                    }}
                />
            )}
        </Card>
    );
});

UnitCard.displayName = 'UnitCard';

